import { Component, ReactNode, ErrorInfo } from "react";
import i18n from "i18next";
import { FiAlertCircle } from "react-icons/fi";
import * as Sentry from "@sentry/react";
import styled from "styled-components";
import { SubsectionCard, Spinner, SectionErrorState } from "@dd-report-components/report/Styles";
import { eventBus } from "../../../../Common/EventBus";

const InlineSpinner = styled(Spinner)`
  border-width: 3px;
`;

interface Props {
  children: ReactNode;
  // Identifies the section in logs so we can tell which one failed.
  sectionName?: string;
  // Optional custom fallback; defaults to a neutral "could not load" card.
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

// Class component is unavoidable here — React's error-catching lifecycle
// (getDerivedStateFromError / componentDidCatch) has no hooks equivalent.
export class SectionErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const sectionName = this.props.sectionName ?? "unknown";
    // eslint-disable-next-line no-console
    console.error(`[DD report section / ${sectionName}]`, error, info);
    Sentry.captureException(error, {
      tags: { area: "dd-report", sectionName },
      contexts: { react: { componentStack: info.componentStack } },
    });
    eventBus.emit("REPORT_SECTION_RENDER_ERROR");
    // Auto-reset after 5s so the block re-renders when the parent has fresh data.
    this.retryTimer = setTimeout(() => this.setState({ hasError: false }), 5000);
  }

  componentWillUnmount(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    return (
      <SubsectionCard>
        <SectionErrorState style={{ display: "flex", alignItems: "center" }}>
          <FiAlertCircle style={{ marginRight: "0.3rem", flexShrink: 0 }} />
          {i18n.t("dueDiligence.legalFiscalReport.sectionErrorBoundary.loadFailedRetrying")}
          <InlineSpinner style={{ marginLeft: "0.5rem", flexShrink: 0 }} />
        </SectionErrorState>
      </SubsectionCard>
    );
  }
}
